import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Plus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { catStyle } from "@/lib/constants";
import { fmtDeadline, fmtDuration } from "@/lib/time";
import TaskDialog from "@/components/planner/TaskDialog";

function MiscRow({ t, who, onOpen, onToggle }) {
  const done = t.status === "done";
  const c = catStyle(t.category);
  return (
    <li className="flex items-center gap-3 py-3 border-b border-[#103080]/10 last:border-0 text-sm" data-testid={`client-misc-task-${t.id}`}>
      <button onClick={() => onToggle(t)} aria-label="Toggle done" data-testid={`client-misc-done-${t.id}`}
        className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 transition-colors duration-200 ${done ? "bg-[#103080] border-[#103080] text-white" : "border-[#103080]/30 hover:border-[#0090D0]"}`}>{done && <Check size={12} />}</button>
      <button onClick={() => onOpen(t)} className={`flex-1 text-left truncate text-[#103080] hover:text-[#0090D0] transition-colors duration-200 ${done ? "line-through opacity-50" : "font-medium"}`}>{t.title}</button>
      <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold shrink-0 hidden sm:inline" style={{ background: c.bg, color: c.text }}>{t.category}</span>
      <span className="text-xs text-[#0F172A]/50 shrink-0 hidden sm:inline">{who || "Unassigned"} · {fmtDuration(t.duration_minutes)}</span>
      <span className="text-xs text-[#0F172A]/50 shrink-0 w-24 text-right">{t.deadline ? `Due ${fmtDeadline(t.deadline)}` : "No deadline"}</span>
    </li>
  );
}

export default function MiscTasks({ client, directory, isAdmin }) {
  const [tasks, setTasks] = useState([]);
  const [dialog, setDialog] = useState(null);
  const people = Object.fromEntries(directory.map((p) => [p.id, p.name]));

  const load = useCallback(() => api.get(`/clients/${client.id}/tasks`).then((r) => setTasks(r.data)).catch((e) => toast.error(formatApiError(e))), [client.id]);
  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (t) => {
    await api.patch(`/tasks/${t.id}`, { status: t.status === "done" ? "todo" : "done" }).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  return (
    <section className="rounded-2xl border border-[#103080]/10 bg-white/70 ambient p-6" data-testid="client-misc-tasks">
      <div className="flex items-center justify-between mb-2 gap-3">
        <div>
          <h3 className="font-serif-brand font-semibold text-xl text-[#103080]">Misc tasks</h3>
          <p className="text-xs text-[#0F172A]/50">One-off planner tasks linked to {client.name}, outside their job cards.</p>
        </div>
        <button onClick={() => setDialog({ client_id: client.id, category: "Services" })} data-testid="client-misc-add"
          className="inline-flex items-center gap-2 rounded-full bg-[#103080] text-white font-medium px-5 h-10 text-sm hover:bg-[#0090D0] transition-colors duration-200 shrink-0"><Plus size={15} /> New task</button>
      </div>
      {tasks.length === 0 ? <p className="text-sm text-[#0F172A]/50 py-4" data-testid="client-misc-empty">No misc tasks for this client yet.</p> : (
        <ul data-testid="client-misc-list">{tasks.map((t) => <MiscRow key={t.id} t={t} who={people[t.user_id]} onOpen={setDialog} onToggle={toggle} />)}</ul>
      )}
      <TaskDialog task={dialog} team={isAdmin ? directory.map((p) => ({ ...p, active: true })) : []} onClose={() => setDialog(null)} onSaved={load} />
    </section>
  );
}
